import React from 'react';
import { UserProfile as UIUserProfile, deriveInitials } from '@aibuild/ui';
import useAuth from '@contexts/useAuth';
import NotificationList from '@components/Notifications/NotificationList';

/**
 * App-side glue around the shared `UserProfile` from `@aibuild/ui`.
 *
 * The shared component is purely presentational — this wrapper:
 *   - reads the signed-in user from our auth context,
 *   - derives a display name + initials,
 *   - composes the notification list as the menu's middle slot,
 *   - and forwards `logout()` to the package's onLogout hook.
 *
 * The render-prop children pattern gives the notification list the menu's
 * `close` handle, so clicking a notification that navigates elsewhere also
 * dismisses the menu — same behaviour as before the package extraction.
 *
 * Returning `null` for the unauthenticated case keeps the top-bar empty
 * during the initial auth bootstrap rather than briefly rendering a "?" avatar.
 */
const UserProfile: React.FC = () => {
  const { user, logout } = useAuth();
  if (!user) return null;

  const displayName = user.displayName?.trim() || user.email;

  return (
    <UIUserProfile
      user={{
        name: displayName,
        email: user.email,
        initials: deriveInitials(user.displayName || user.email),
      }}
      onLogout={logout}
    >
      {({ close }) => (
        <NotificationList
          onNotificationOpen={close}
          dataTestId="user-profile__notifications"
        />
      )}
    </UIUserProfile>
  );
};

export default UserProfile;
